<script lang="ts" setup>
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserRole } from "@/utils/useUserRole";
import { requiredValidator } from "@validators";
import { toast } from "vue3-toastify";

// Interface
interface Emit {
  (e: "closeDialog", value: boolean): void;
}

interface Props {
  isVisible?: boolean;
  docketId: string;
}

// Props
const props = defineProps<Props>();

// Emit
const emit = defineEmits<Emit>();

// Store
const userRole = useUserRole();

const { permissionListMap } = storeToRefs(userRole);

// Data
const email = ref<string>(pb.authStore.model?.email);
const userId = ref<string>(pb?.authStore?.model?.id);
const refReceiptVForm = ref<any>();
const docketId = ref<string>("");
const isHistoryDialogOpen = ref<boolean>(false);

// Method
const emailReceipt = async () => {
  const { valid } = await refReceiptVForm.value?.validate();
  if (!valid) return;
  if (docketId.value) {
    const data = {
      docketID: docketId.value,
      email: email.value,
      requiresEmail: true,
      createdBy: userId.value,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Email Receipt Sent", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      closeDialog();
    }
  }
};

// Update requiresPrinting in docketPayments
const printReceipt = async () => {
  if (docketId.value) {
    const data = {
      docketID: docketId.value,
      email: email.value,
      requiresPrinting: true,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Receipt Printed", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      closeDialog();
    }
  }
};

const closeDialog = () => {
  emit("closeDialog", false);
};

// watch
watchEffect(() => {
  if (props?.docketId) {
    docketId.value = props?.docketId;
  }
});
</script>

<template>
  <VDialog
    persistent
    :model-value="props.isVisible"
    no-click-animation
    width="600"
    @update:is-drawer-open="closeDialog"
  >
    <VCard flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle> Create Receipt </VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog"
        ></VBtn>
      </div>
      <VCardText>
        <VResponsive class="mx-auto mt-4" max-width="344">
          <div class="mt-2">
            <VForm ref="refReceiptVForm" class="d-flex">
              <VTextField
                v-model="email"
                :rules="[requiredValidator]"
                label="Email Address"
                class="mr-5"
              />
              <div class="d-flex align-center">
                <IconBtn
                  :disabled="
                    permissionListMap['dockets'] &&
                    !permissionListMap['dockets']?.includes('View')
                  "
                  color="error"
                  class="d-flex"
                  @click="isHistoryDialogOpen = true"
                >
                  <VIcon icon="mdi-history" />
                  <VTooltip activator="parent" open-delay="400">
                    History
                  </VTooltip>
                </IconBtn>
              </div>
            </VForm>
          </div>
        </VResponsive>
        <div class="d-flex justify-center flex-wrap gap-4 my-4 mt-6">
          <VBtn color="primary" @click="emailReceipt"> Email Receipt </VBtn>
          <VBtn color="primary" @click="printReceipt"> Print Receipt </VBtn>
          <VBtn color="error" @click="closeDialog"> Close </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- History dialog -->
  <HistoryDialog
    :is-visible="isHistoryDialogOpen"
    :docket-id="docketId"
    @close-dialog="
      () => {
        isHistoryDialogOpen = false;
      }
    "
  />
</template>
